import * as _ from 'lodash';
import * as peg from 'pegjs';
import {
  SlickGrid,
  Column,
  Formatter,
  FormatterEx,
  SlickGridModel,
} from 'slickgrid';
import { containsValue, isEmpty, assertNonNullish } from './helpers';

const NULL_CLAUSES = ['ures', 'none', 'nil', 'null'],
  TRUTHY_CLAUSES = ['yes', 'igen', 'true'],
  FALSY_CLAUSES = ['no', 'nem', 'false'];

const NumParser = peg.generate(`
start = NumGe
  / NumLe
  / NumG
  / NumL
  / NumRange
  / NumEq
  / NumNotEq


NumNotEq
  = left:Number "!=" right:Number { return left !== right; }

NumEq
  = left:Number "=" right:Number { return left === right; }

NumGe
  = left:Number ">=" right:Number { return left >= right; }

NumLe
  = left:Number "<=" right:Number { return left <= right; }

NumG
  = left:Number ">" right:Number { return left > right; }

NumL
  = left:Number "<" right:Number { return left < right; }

NumRange
  = left:Number "=" min:Number "-" max:Number { return min <= left && left <= max; }


Number
  = Float / Integer

Integer
  = _ [0-9]+ { return parseInt(text(), 10); }

Float
  = _ Integer+ '.' Integer+ { return parseFloat(text()); }

// optional whitespace
_  = [ ]*

// mandatory whitespace
__ = [ ]+
`);

export function getCellValue<T = Dictionary<any>>(
  data: T,
  column: Column<T>,
  grid: SlickGrid,
): string | null {
  const field = column.field;
  assertNonNullish(column.field, 'Column field is required.');

  let cellValue = data[_.toString(column.field)];

  const formatter = column.formatter as FormatterEx<T>;

  if (formatter && formatter.getCellValue) {
    return formatter.getCellValue(
      -1,
      -1,
      cellValue,
      column,
      data,
      grid,
    ) as string;
  }

  if (formatter && formatter.filterFormattedData) {
    return formatter(-1, -1, cellValue, column, data, grid) as string;
  }

  if (_.isArray(cellValue)) {
    cellValue = cellValue.join(', ');
  }

  if (isEmpty(cellValue)) {
    return null;
  }

  return cellValue;
}

function removeDiacritics(value: string) {
  /* credits: https://stackoverflow.com/a/37511463/1737327
   * 1. normalize()ing to NFD Unicode normal form decomposes combined
   *    graphemes into the combination of simple ones.
   *    The è of Crème ends up expressed as e +  ̀.
   * 2. Using a regex character class to match the U+0300 → U+036F range,
   *    it is now trivial to globally get rid of the diacritics,
   *    which the Unicode standard conveniently groups as the
   *    Combining Diacritical Marks Unicode block.
   */
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function evalStringMatch(input: string, value: string | null) {
  value = _.isNull(value) ? value : removeDiacritics(value).toLowerCase();
  input = removeDiacritics(input).toLowerCase();

  const token_0 = input[0];

  if (value === null || typeof value === 'undefined') {
    return false;
  }

  if (token_0 === '=') {
    return value === input.substr(1);
  }

  if (token_0 === '^') {
    return _.startsWith(value, input.substr(1));
  }

  if (input[input.length - 1] === '$') {
    return _.endsWith(value, input.substring(0, input.length - 1));
  }

  if (NULL_CLAUSES.indexOf(input) > -1) {
    return value === null || value === '';
  }

  if (TRUTHY_CLAUSES.indexOf(input) > -1) {
    return value === 'true';
  }

  if (FALSY_CLAUSES.indexOf(input) > -1) {
    return value === 'false';
  }

  return value.indexOf(input) > -1;
}

function stringMatches(input: string, value: string | null) {
  const isNot = input && input[0] === '!',
    result = evalStringMatch(isNot ? input.substring(1) : input, value);

  return isNot ? !result : result;
}

function matches(input: string, value: string | number | boolean | null) {
  input = input.trim();

  try {
    return NumParser.parse(`${value}${input}`);
  } catch (error) {}

  return stringMatches(input, _.isNull(value) ? null : value.toString());
}

function simpleFilterCallback<T>(cls: DataFilter<T>) {
  /**
   * Filters the dataview
   * @param  {Object} item  The row's dataitem.
   * @return {Boolean}      False if the row should be filtered out.
   */
  return function (item: T) {
    if (cls.filters) {
      const grid = cls.grid,
        columns = grid.getColumns();

      // all non-empty filters must match
      return _.every(cls.filters, (filterValue, columnId) => {
        if (isEmpty(filterValue)) {
          return true;
        }

        const col = columns[grid.getColumnIndex(columnId)],
          value = getCellValue(item, col, cls.grid);

        return matches(filterValue, value);
      });
    }

    return true;
  };
}

export class DataFilter<T> {
  private $filters: Dictionary<string>;

  readonly grid: SlickGrid<T>;

  readonly callback: (item: T) => boolean;

  constructor(grid: SlickGrid<any>, filters?: Dictionary<string>) {
    this.grid = grid;
    this.callback = simpleFilterCallback(this);
  }

  setFilters(value: Dictionary<string>) {
    this.$filters = _.reduce(
      value,
      (m, v, k) => {
        if (!isEmpty(v)) {
          m[k] = v;
        }
        return m;
      },
      {} as Dictionary<any>,
    );
  }

  get filters() {
    return this.$filters;
  }
}
